import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, btn, btnGhost, inputClass } from "@/pages/admin/email/EmailLayout";

export default function EmailTags() {
  const [tags, setTags] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api.get("/admin/email/tags").then((r) => setTags(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/email/tags", { name });
      setName("");
      toast.success("Tag created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const rename = async (id) => {
    try {
      await api.patch(`/admin/email/tags/${id}`, { name: editName });
      setEditing(null);
      toast.success("Tag renamed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the tag "${t.name}"? Subscribers are kept, only the tag is removed.`))
      return;
    await api.delete(`/admin/email/tags/${t.id}`);
    toast.success("Tag deleted");
    load();
  };

  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8" data-testid="email-tags">
      <Card title="Tags and audience segments" testid="email-tags-list">
        {tags.map((t) => (
          <div
            key={t.id}
            className="flex items-center justify-between gap-4 py-3 border-b border-line last:border-0"
            data-testid={`tag-row-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {editing === t.id ? (
              <>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={`${inputClass} max-w-[240px]`}
                  data-testid="tag-rename-input"
                />
                <span className="flex gap-2">
                  <button onClick={() => rename(t.id)} className={btn} data-testid="tag-rename-save">
                    Save
                  </button>
                  <button onClick={() => setEditing(null)} className={btnGhost}>
                    Cancel
                  </button>
                </span>
              </>
            ) : (
              <>
                <span className="text-[14px] text-charcoal">
                  {t.name}
                  <span className="text-[12px] font-light text-[#8A867F] ml-3">
                    {t.subscribers} active
                  </span>
                </span>
                <span className="flex gap-4 whitespace-nowrap">
                  <button
                    onClick={() => {
                      setEditing(t.id);
                      setEditName(t.name);
                    }}
                    data-testid={`tag-edit-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-[11px] tracking-[0.12em] uppercase text-sage hover:text-sage-dark"
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => remove(t)}
                    data-testid={`tag-delete-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-[11px] tracking-[0.12em] uppercase text-[#991B1B]/70 hover:text-[#991B1B]"
                  >
                    Delete
                  </button>
                </span>
              </>
            )}
          </div>
        ))}
      </Card>

      <div className="space-y-8">
        <Card title="Create a tag" testid="email-tag-create">
          <form onSubmit={create}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Fireplaces"
              data-testid="tag-create-input"
              className={inputClass}
            />
            <button type="submit" className={`${btn} mt-4`} data-testid="tag-create-submit">
              Add tag
            </button>
          </form>
        </Card>
        <Card title="How segments work" testid="email-tag-help">
          <p className="text-[13px] font-light text-[#5A5A5A] leading-relaxed">
            A campaign can be addressed to everyone active, to one or more tags, or to a hand-picked
            set of subscribers. Choosing the tag <em>Saunas</em> on a campaign sends only to people
            carrying that tag. Visitors pick their own interests on the signup forms and can change
            them later from any email footer.
          </p>
        </Card>
      </div>
    </div>
  );
}
